// ----------------------------------------------------------------------
// Форматирование для узбекского рынка (UZS)
// ----------------------------------------------------------------------

export const fCurrency = (n) => {
  const v = Number(n || 0);
  return `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} сум`;
};

export const fMoney = (n) => Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/** Компактно: 1 250 000 → 1,25 млн */
export const fShortMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.', ',')} млн`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1000)} тыс`;
  return String(Math.round(v));
};

export const fNumber = (n) => Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 });

export const fPercent = (n) => `${Number(n || 0) > 0 ? '' : ''}${Number(n || 0).toFixed(1)}%`;

export const fSignedPercent = (n) => {
  const v = Number(n || 0);
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
};

export const fDate = (d) =>
  d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export const fDateShort = (d) =>
  d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }) : '—';

export const fTime = (d) =>
  d ? new Date(d).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—';

export const fDateTime = (d) => (d ? `${fDate(d)} ${fTime(d)}` : '—');

/** «5 минут назад» */
export const fFromNow = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days} дн назад`;
  return fDate(d);
};

// ----------------------------------------------------------------------
// Словари
// ----------------------------------------------------------------------

export const ORDER_STATUS = {
  NEW: { label: 'Новый', color: 'info' },
  CONFIRMED: { label: 'Подтверждён', color: 'primary' },
  COOKING: { label: 'Готовится', color: 'warning' },
  READY: { label: 'Готов', color: 'success' },
  DELIVERING: { label: 'Доставляется', color: 'secondary' },
  COMPLETED: { label: 'Выдан', color: 'default' },
  CANCELLED: { label: 'Отменён', color: 'error' },
};

export const ORDER_TYPE = {
  PICKUP: { label: 'Самовывоз', icon: 'mdi:walk' },
  DELIVERY: { label: 'Доставка на этаж', icon: 'mdi:elevator' },
  DINE_IN: { label: 'В зале', icon: 'mdi:table-chair' },
};

export const PAYMENT_PROVIDER = {
  CLICK: 'Click',
  PAYME: 'Payme',
  CASHBACK: 'Кэшбэк',
  BALANCE: 'Баланс',
  CORPORATE: 'Компания',
  CASH: 'Наличные',
};

export const PAYMENT_STATUS = {
  PENDING: { label: 'Ожидает', color: 'warning' },
  PAID: { label: 'Оплачен', color: 'success' },
  FAILED: { label: 'Ошибка', color: 'error' },
  REFUNDED: { label: 'Возврат', color: 'default' },
  PARTIAL: { label: 'Частично', color: 'info' },
};

export const MEASURE_UNITS = {
  KG: 'кг',
  GRAM: 'г',
  LITER: 'л',
  ML: 'мл',
  PIECE: 'шт',
  PACK: 'упак',
  BOX: 'ящик',
};

export const INGREDIENT_CATEGORIES = {
  MEAT: 'Мясо и птица',
  VEGETABLE: 'Овощи',
  FRUIT: 'Фрукты',
  DAIRY: 'Молочные продукты',
  GROCERY: 'Бакалея',
  BAKERY: 'Хлеб и выпечка',
  SPICE: 'Специи',
  BEVERAGE: 'Напитки',
  OTHER: 'Прочее',
};

export const MOVEMENT_TYPES = {
  IN: { label: 'Приход', color: 'success' },
  OUT: { label: 'Расход', color: 'info' },
  WASTE: { label: 'Списание', color: 'error' },
  CORRECTION: { label: 'Инвентаризация', color: 'warning' },
  RETURN: { label: 'Возврат', color: 'default' },
};

export const SUBSIDY_TYPES = {
  NONE: 'Без субсидии',
  PERCENT: 'Процент от заказа',
  DAILY_LIMIT: 'Дневной лимит',
  FULL: 'Полностью за счёт компании',
};

export const ADMIN_ROLES = {
  SUPER_ADMIN: 'Супер-админ',
  KITCHEN_ADMIN: 'Управляющий кухней',
  CHEF: 'Повар',
  CASHIER: 'Кассир',
  HR: 'HR компании',
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: { label: 'Активна', color: 'success' },
  PAUSED: { label: 'На паузе', color: 'warning' },
  FINISHED: { label: 'Завершена', color: 'default' },
  CANCELLED: { label: 'Отменена', color: 'error' },
};

export const BROADCAST_AUDIENCE = {
  ALL: 'Все сотрудники',
  SUBSCRIBERS: 'С активным пакетом',
  NON_SUBSCRIBERS: 'Без пакета',
  DEPARTMENT: 'Отдел',
  INACTIVE: 'Не заказывали 7+ дней',
  ORGANIZATION: 'Организация',
};

export const BROADCAST_STATUS = {
  DRAFT: { label: 'Черновик', color: 'default' },
  SCHEDULED: { label: 'Запланирована', color: 'info' },
  SENDING: { label: 'Отправляется', color: 'warning' },
  SENT: { label: 'Отправлена', color: 'success' },
  FAILED: { label: 'Ошибка', color: 'error' },
};

export const PROMOTION_TYPES = {
  HAPPY_HOUR: 'Счастливые часы',
  PERCENT: 'Процент',
  FIXED: 'Фиксированная сумма',
};

export const PROMOTION_SCOPES = {
  ALL: 'Всё меню',
  CATEGORY: 'Категории',
  DISH: 'Отдельные блюда',
};

export const MENU_CLASS = {
  star: { label: 'Звезда', color: 'success', hint: 'Популярно и прибыльно — продвигать' },
  workhorse: { label: 'Рабочая лошадка', color: 'info', hint: 'Популярно, но маржа низкая — поднять цену' },
  puzzle: { label: 'Загадка', color: 'warning', hint: 'Прибыльно, но мало заказов — рекламировать' },
  dog: { label: 'Балласт', color: 'error', hint: 'Мало заказов и низкая маржа — вывести из меню' },
};

export const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
export const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
